'use client';

import { useEffect, useRef } from 'react';

type PollingTaskOptions = {
  enabled: boolean;
  intervalMs: number;
  runImmediately?: boolean;
  /**
   * Skip ticks while the tab is hidden (default). A floor of parked tabs
   * polling heavy endpoints adds real DB load for nobody; the next tick after
   * the tab becomes visible fires immediately. Pass false for polls with
   * server-side effects that must continue in the background — e.g. the
   * dialer resume poll renews the contact-lock lease.
   */
  pauseWhenHidden?: boolean;
  onError?: (error: unknown) => void;
};

export function usePollingTask(
  task: () => Promise<void> | void,
  {
    enabled,
    intervalMs,
    runImmediately = true,
    pauseWhenHidden = true,
    onError,
  }: PollingTaskOptions
): void {
  const taskRef = useRef(task);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    taskRef.current = task;
  }, [task]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let disposed = false;
    let running = false;
    let rerunRequested = false;

    async function runTask(): Promise<void> {
      if (running) {
        rerunRequested = true;
        return;
      }
      running = true;
      try {
        await taskRef.current();
      } catch (error) {
        if (!disposed) {
          onErrorRef.current?.(error);
        }
      } finally {
        running = false;
        if (!disposed && rerunRequested) {
          rerunRequested = false;
          void runTask();
        }
      }
    }

    if (runImmediately) {
      void runTask();
    }

    const interval = window.setInterval(() => {
      if (pauseWhenHidden && document.visibilityState === 'hidden') {
        return;
      }
      void runTask();
    }, intervalMs);

    const onVisibilityChange = () => {
      if (pauseWhenHidden && document.visibilityState === 'visible') {
        void runTask();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, intervalMs, runImmediately, pauseWhenHidden]);
}
